// Visible "VIBE JAM PORTAL" ring in the outdoor lobby. Complements
// the thematic Dreamer's Cabin bed (which also redirects to the
// webring) — a pattern-matched green ring is what the Vibe Jam 2026
// portal-sample.js expects visually, so judges + cross-game visitors
// recognize it on sight. Both portals redirect to the same URL; both
// increment MOST PORTAL TRANSFERS.

import * as THREE from 'three';

export type VibejamPortalHandle = {
  group: THREE.Group;
  position: THREE.Vector3;
  interactRadius: number;
  isNearby: (x: number, z: number) => boolean;
  update: (dt: number) => void;
  dispose: () => void;
};

const INTERACT_R = 2.4;

export function buildVibejamPortal(
  parent: THREE.Object3D,
  origin: { x: number; z: number },
): VibejamPortalHandle {
  const group = new THREE.Group();
  group.position.set(origin.x, 0, origin.z);

  // Vertical green torus — "ring you walk through".
  const torus = new THREE.Mesh(
    new THREE.TorusGeometry(1.6, 0.18, 12, 48),
    new THREE.MeshStandardMaterial({
      color: 0x22c55e,
      emissive: 0x22c55e,
      emissiveIntensity: 1.8,
      metalness: 0.3,
      roughness: 0.4,
    }),
  );
  torus.rotation.y = Math.PI / 2; // face south so player walks through from z < origin.z
  torus.position.y = 1.8;
  group.add(torus);

  // Inner swirl disk — translucent green with additive blending so the
  // ring "contains" a portal plane.
  const disk = new THREE.Mesh(
    new THREE.CircleGeometry(1.4, 40),
    new THREE.MeshBasicMaterial({
      color: 0x86efac,
      transparent: true,
      opacity: 0.35,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
    }),
  );
  disk.rotation.y = Math.PI / 2;
  disk.position.y = 1.8;
  group.add(disk);

  // Glow disk at base so the ring reads as grounded even from a
  // distance when the torus is off-screen.
  const glow = new THREE.Mesh(
    new THREE.CircleGeometry(2.2, 32),
    new THREE.MeshBasicMaterial({
      color: 0x22c55e,
      transparent: true,
      opacity: 0.35,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }),
  );
  glow.rotation.x = -Math.PI / 2;
  glow.position.y = 0.03;
  group.add(glow);

  // Floating label sprite so players immediately know what it is.
  const label = makeLabelSprite('VIBE JAM PORTAL');
  label.position.set(0, 3.6, 0);
  label.scale.set(3.6, 0.9, 1);
  group.add(label);

  parent.add(group);

  let tSec = 0;
  return {
    group,
    position: group.position,
    interactRadius: INTERACT_R,
    isNearby(x: number, z: number): boolean {
      const dx = x - group.position.x;
      const dz = z - group.position.z;
      return dx * dx + dz * dz <= INTERACT_R * INTERACT_R;
    },
    update(dt: number): void {
      tSec += dt;
      const pulse = 1 + Math.sin(tSec * 2.4) * 0.06;
      torus.scale.set(pulse, pulse, 1);
      disk.material.opacity = 0.28 + Math.sin(tSec * 1.6) * 0.08;
      glow.material.opacity = 0.28 + Math.sin(tSec * 1.1) * 0.08;
      torus.rotation.z = tSec * 0.6;
    },
    dispose(): void {
      torus.geometry.dispose();
      (torus.material as THREE.Material).dispose();
      disk.geometry.dispose();
      disk.material.dispose();
      glow.geometry.dispose();
      glow.material.dispose();
      parent.remove(group);
    },
  };
}

function makeLabelSprite(text: string): THREE.Sprite {
  const c = document.createElement('canvas');
  c.width = 512; c.height = 128;
  const ctx = c.getContext('2d')!;
  ctx.clearRect(0, 0, c.width, c.height);
  ctx.fillStyle = 'rgba(15,23,42,.85)';
  ctx.strokeStyle = '#86efac';
  ctx.lineWidth = 4;
  const pad = 16;
  roundRect(ctx, pad, pad, c.width - pad * 2, c.height - pad * 2, 20);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = '#86efac';
  ctx.font = '900 56px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.shadowColor = 'rgba(34,197,94,.75)';
  ctx.shadowBlur = 16;
  ctx.fillText(text, c.width / 2, c.height / 2 + 2);
  const tex = new THREE.CanvasTexture(c);
  tex.needsUpdate = true;
  const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false }),
  );
  return sprite;
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
